/**
 * [ui1006, paczka KIT] REJESTR OBSZARÓW — „Integracje i połączenia” (`/admin/integrations/…`).
 *
 * Jedno źródło nawigacji strony: 5 grup, 18 obszarów (1 + 4 + 3 + 4 + 6) — rozdz. 5 projektu ui1006 — zamiast siedmiu
 * zakładek bez adresu i dziewięciu akordeonów. Wejście bez zmian: ranga administratora + PIN (pilnuje router panelu,
 * także dla adresów głębokich).
 *
 * Słowa: spółka-obiekt to zawsze `?karta=ID`; `?spolka=` jest wyłącznie filtrem list (Poczta, Synchronizacja kont, Licencje).
 *
 * `aktywna: false` — paczka KIT tylko zakłada rejestr; stronę przełącza paczka INT (robiona po rozbudowie Office 365).
 * Do tego czasu strona żyje pod adresem bazowym po staremu, a stare slugi `/admin/domains` i `/admin/microsoft365`
 * prowadzą na adres bazowy; po przełączeniu — na obszary z `stareSlugi`.
 */
import {
    FiList, FiMail, FiMessageSquare, FiUserCheck, FiAlertTriangle, FiBriefcase, FiInbox, FiShare2, FiLink,
    FiRefreshCw, FiAward, FiShield, FiPlayCircle, FiActivity, FiMapPin, FiLock, FiEyeOff, FiHelpCircle,
} from "react-icons/fi";
import { GRUPA_PRACY, GRUPA_USTAWIEN, grupaStartu, grupaPracy, grupaUstawien, obszar, obszarStartu } from "./ksztalt";

const GRUPA_DANYCH = "dane-spolek";
const GRUPA_POLACZEN = "polaczenia";

/** Osiem systemów zewnętrznych (wartości `?system=`) i części karty każdego z nich (`&czesc=`). */
export const CZESCI_SYSTEMOW = {
    microsoft365: ["konta", "diagnostyka", "uprawnienia", "przelozeni", "grozne"],
    sms: ["konta", "komunikaty", "grozne"],
    teamviewer: ["polaczenie", "grupy", "komputery", "import", "grozne"],
    ocr: ["ustawienia", "rozliczenie"],
    ksef: ["dane"],
    enova: ["dane"],
    skalemex: ["dane"],
    "wykaz-ksc": ["klucze"],
};

/** Dawny akordeon „Integracji technicznych” (`location.state.rozwin`) → system zewnętrzny. Tabela 5.7 projektu. */
const SYSTEM_Z_ROZWINIECIA = {
    office365: "microsoft365",
    teamviewer: "teamviewer",
    sms: "sms",
    ocr: "ocr",
    ksef: "ksef",
    enova: "enova",
    skalemex: "skalemex",
    wykazksc: "wykaz-ksc",
};

/**
 * STARE WEJŚCIA (tabela 5.7 projektu) — czysta funkcja: działa tylko na adresie bazowym strony.
 * Dawna strona nie miała adresów sekcji — sekcję niósł stan nawigacji (`location.state.section`, `rozwin`, `companyId`).
 * Wołający spoza plików paczki INT zostają na tym stanie (decyzja D-22), więc to przekierowanie jest trwałe.
 */
const stareAdresy = ({ reszta, parametry, state }) => {
    if (reszta) return null;
    const dopisz = (zmiany) => {
        const nowe = new URLSearchParams(parametry);
        Object.entries(zmiany).forEach(([nazwa, wartosc]) => {
            if (wartosc !== null && wartosc !== undefined && wartosc !== "") nowe.set(nazwa, String(wartosc));
        });
        return nowe;
    };

    // Powrót z TeamViewera: dostawca odsyła na adres bazowy z `?code=&state=` (adres zarejestrowany — nie do zmiany).
    if (parametry.has("code") && parametry.has("state")) {
        return { obszar: "systemy", zapytanie: dopisz({ system: "teamviewer", czesc: "polaczenie" }) };
    }

    const sekcja = state && state.section;
    const rozwin = state && state.rozwin;
    switch (sekcja) {
        case "skrzynki":
            return { obszar: "poczta", zapytanie: dopisz({}) };
        case "lista-logowan":
            return { obszar: "logowanie", zapytanie: dopisz({}) };
        case "integracje":
            if (rozwin === "emailSync") return { obszar: "skrzynki-dzialow", zapytanie: dopisz({}) };
            return { obszar: "systemy", zapytanie: dopisz({ system: SYSTEM_Z_ROZWINIECIA[rozwin] || null }) };
        case "diagnostyka":
            return { obszar: "poczta", zapytanie: dopisz({ widok: "z-bledem" }) };
        case "crossorg":
            return { obszar: "udostepnienia", zapytanie: dopisz({}) };
        case "monitoring":
            return { obszar: "ustawienia-aktywnosc", zapytanie: dopisz({}) };
        case "reguly-incydentow": {
            const krok = state.krok === "zgody" || state.krok === "reaktor" ? state.krok : null;
            return { obszar: "bezpieczenstwo", zapytanie: dopisz({ karta: state.companyId, krok: state.companyId ? krok : null }) };
        }
        default:
            return null;
    }
};

const integracje = {
    strona: "integrations",
    tytul: "Integracje i połączenia",
    /** Tytuł, pod którym strona żyje DO przełączenia przez paczkę INT (tytuł kafelka panelu) — „Powrót” i ekran linku go używają. */
    tytulDotychczasowy: "Microsoft 365 — integracje i skrzynki",
    zdanie: "Pilnuje, żeby poczta, SMS-y i połączenia z innymi systemami działały.",
    baza: { pl: "/admin/integrations", en: "/admin/integrations" },
    kluczStrony: "AdminIntegrations",
    aktywna: false,

    grupy: [
        grupaStartu(),
        grupaPracy(),
        { id: GRUPA_DANYCH, etykieta: "Dane spółek" },
        { id: GRUPA_POLACZEN, etykieta: "Połączenia" },
        grupaUstawien(),
    ],

    obszary: [
        obszarStartu({
            kluczTrasy: "adminModule",
            zdanie: "Pilnuje, żeby poczta, SMS-y i połączenia z innymi systemami działały.",
            ikona: FiList,
            filtry: [],
            slowa: ["do załatwienia", "co nie działa", "połączenia", "start"],
        }),

        // ── Praca ──────────────────────────────────────────────────────────────────────────────
        obszar({
            id: "poczta", grupa: GRUPA_PRACY, segment: { pl: "poczta", en: "mail" }, kluczTrasy: "adminIntPoczta",
            etykieta: "Poczta", zdanie: "Tu sprawdzasz, czy poczta wychodzi, i prowadzisz skrzynki spółek.",
            ikona: FiMail, licznik: true,
            widoki: [
                { id: "wszystkie", etykieta: "Wszystkie", licznik: true },
                { id: "z-bledem", etykieta: "Z błędem", licznik: true },
                { id: "wylaczone", etykieta: "Wyłączone", licznik: true },
            ],
            filtry: ["spolka"],
            obiekty: [{
                parametr: "skrzynka", rodzaj: "karta", nazwa: "skrzynka", dopelniacz: "skrzynki",
                czesci: ["stan", "dane", "obce-domeny", "grozne"],
            }],
            kreatory: [{ nazwa: "skrzynka", tytul: "Dodaj skrzynkę", kroki: 4 }],
            pasek: { akcjaGlowna: "Dodaj skrzynkę" },
            slowa: ["skrzynki i tożsamość", "skrzynki", "domeny", "wysyłka", "diagnostyka poczty", "wiadomość próbna"],
        }),
        obszar({
            id: "sms", grupa: GRUPA_PRACY, segment: { pl: "sms", en: "sms" }, kluczTrasy: "adminIntSms",
            etykieta: "SMS-y", zdanie: "Tu widzisz, co wyszło, co stoi w kolejce i co się nie udało.",
            ikona: FiMessageSquare, wzor: "S2 + S7", licznik: true,
            widoki: [
                { id: "wszystkie", etykieta: "Wszystkie" },
                { id: "w-kolejce", etykieta: "W kolejce", licznik: true },
                { id: "bledy", etykieta: "Błędy", licznik: true },
                { id: "wstrzymane", etykieta: "Wstrzymane", licznik: true },
            ],
            obiekty: [{ parametr: "wpis", rodzaj: "szuflada", nazwa: "SMS", dopelniacz: "SMS-a" }],
            glowa: { zdanieStanu: "w kolejce: 3" },
            pasek: { wiecej: ["Otwórz bramkę SMS →"] },
            slowa: ["bramka SMS", "kolejka SMS", "wysłane SMS-y", "ponów wysyłkę"],
        }),
        obszar({
            id: "logowanie", grupa: GRUPA_PRACY, segment: { pl: "logowanie", en: "sign-in" }, kluczTrasy: "adminIntLogowanie",
            etykieta: "Kto może się logować", zdanie: "Tu pilnujesz listy dozwolonych logowań każdej spółki.",
            ikona: FiUserCheck,
            obiekty: [{
                parametr: "karta", rodzaj: "karta", nazwa: "lista logowań spółki", dopelniacz: "listy logowań spółki",
                czesci: ["osoby", "odmowy", "grozne"],
            }],
            slowa: ["lista dozwolonych logowań", "whitelista", "odmowy logowania", "kto ma dostęp"],
        }),
        obszar({
            id: "tryb-testowy", grupa: GRUPA_PRACY, segment: { pl: "tryb-testowy", en: "test-mode" }, kluczTrasy: "adminIntTrybTestowy",
            etykieta: "Tryb testowy powiadomień", zdanie: "Tu włączasz i wyłączasz tryb testowy powiadomień każdej spółki.",
            ikona: FiAlertTriangle, wzor: "S2 + S7",
            obiekty: [{ parametr: "karta", rodzaj: "szuflada", nazwa: "tryb testowy spółki", dopelniacz: "trybu testowego spółki" }],
            slowa: ["tryb testowy", "e-maile na jedną skrzynkę", "wstrzymane SMS-y", "testy powiadomień"],
        }),

        // ── Dane spółek ────────────────────────────────────────────────────────────────────────
        obszar({
            id: "spolki", grupa: GRUPA_DANYCH, segment: { pl: "spolki", en: "companies" }, kluczTrasy: "adminIntSpolki",
            etykieta: "Spółki i ich dane", zdanie: "Tu prowadzisz dane spółek używane w stopkach, umowach i pismach.",
            ikona: FiBriefcase,
            obiekty: [{
                parametr: "karta", rodzaj: "karta", nazwa: "karta spółki", dopelniacz: "karty spółki",
                czesci: ["dane", "adres", "biura", "rejestr", "reprezentacja", "klauzule", "skrzynki"],
            }],
            kreatory: [{ nazwa: "spolka", tytul: "Dodaj spółkę", kroki: 2 }],
            pasek: { akcjaGlowna: "Dodaj spółkę" },
            slowa: ["firmy", "dane firmy", "NIP", "biura", "reprezentant", "klauzule"],
        }),
        obszar({
            id: "skrzynki-dzialow", grupa: GRUPA_DANYCH, segment: { pl: "skrzynki-dzialow", en: "department-mailboxes" }, kluczTrasy: "adminIntSkrzynkiDzialow",
            etykieta: "Skrzynki działów", zdanie: "Tu widzisz, która skrzynka obsługuje który dział.",
            ikona: FiInbox, wzor: "S2 (odczyt)",
            pasek: { wiecej: ["Przejdź do Konfiguratora zgłoszeń →"] },
            slowa: ["synchronizacja skrzynek", "skrzynka działu", "import poczty", "routing modułów"],
        }),
        obszar({
            id: "udostepnienia", grupa: GRUPA_DANYCH, segment: { pl: "udostepnienia", en: "sharing" }, kluczTrasy: "adminIntUdostepnienia",
            etykieta: "Udostępnienia między spółkami", zdanie: "Tu pozwalasz pracownikom jednej spółki widzieć okna zgłoszeń drugiej.",
            ikona: FiShare2, wzor: "S2 + S7",
            obiekty: [{ parametr: "udostepnienie", rodzaj: "szuflada", nazwa: "udostępnienie", dopelniacz: "udostępnienia" }],
            kreatory: [{ nazwa: "udostepnienie", tytul: "Dodaj udostępnienie", kroki: 2 }],
            pasek: { akcjaGlowna: "Dodaj udostępnienie" },
            slowa: ["cross-org", "firmy", "udostępnianie okien zgłoszeń", "wstrzymaj udostępnienie"],
        }),

        // ── Połączenia ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "systemy", grupa: GRUPA_POLACZEN, segment: { pl: "systemy", en: "systems" }, kluczTrasy: "adminIntSystemy",
            etykieta: "Systemy zewnętrzne", zdanie: "Tu widzisz stan każdego połączenia i wchodzisz do jego ustawień.",
            ikona: FiLink,
            obiekty: [
                {
                    parametr: "system", rodzaj: "karta", nazwa: "system zewnętrzny", dopelniacz: "systemu zewnętrznego",
                    wartosci: Object.keys(CZESCI_SYSTEMOW),
                    czesciWgWartosci: CZESCI_SYSTEMOW,
                    czesci: [...new Set(Object.values(CZESCI_SYSTEMOW).flat())],
                    kreatorOsadzony: { wartosc: "teamviewer", czesc: "import" },
                },
                {
                    parametr: "konto", rodzaj: "karta", wymaga: "system", nazwa: "konto bramki SMS", dopelniacz: "konta bramki SMS",
                    czesci: ["dane", "serwery", "spolki", "grozne"],
                },
            ],
            slowa: [
                "integracje techniczne", "Microsoft 365", "diagnostyka Microsoft 365", "TeamViewer", "bramka SMS", "OCR", "KSeF",
                "Enova", "Skalemex", "Wykaz KSC", "klucze źródeł", "uprawnienia aplikacji",
            ],
        }),
        obszar({
            id: "synchronizacja-kont", grupa: GRUPA_POLACZEN, segment: { pl: "synchronizacja-kont", en: "account-sync" }, kluczTrasy: "adminIntSynchronizacja",
            etykieta: "Synchronizacja kont", zdanie: "Tu synchronizujesz konta z Microsoft 365 i widzisz wynik.",
            ikona: FiRefreshCw, wzor: "jedna karta",
            filtry: ["spolka"],
            slowa: ["synchronizuj teraz", "konta pracowników", "książka adresowa", "Microsoft 365"],
        }),
        obszar({
            id: "licencje", grupa: GRUPA_POLACZEN, segment: { pl: "licencje", en: "licenses" }, kluczTrasy: "adminIntLicencje",
            etykieta: "Licencje", zdanie: "Tu sprawdzasz, czy starczy licencji Microsoft 365.",
            ikona: FiAward, wzor: "S2 + S7",
            filtry: ["spolka"],
            obiekty: [{ parametr: "licencja", rodzaj: "szuflada", nazwa: "licencja", dopelniacz: "licencji" }],
            slowa: ["licencje Microsoft 365", "wykorzystanie licencji", "wolne licencje"],
        }),
        obszar({
            id: "bezpieczenstwo", grupa: GRUPA_POLACZEN, segment: { pl: "bezpieczenstwo-office-365", en: "office-365-security" }, kluczTrasy: "adminIntBezpieczenstwo",
            etykieta: "Bezpieczeństwo Office 365", zdanie: "Tu uruchamiasz pilnowanie bezpieczeństwa chmury w każdej spółce.",
            ikona: FiShield, wzor: "S2 → S8",
            obiekty: [{
                parametr: "karta", rodzaj: "karta-z-krokami",
                nazwa: "uruchomienie bezpieczeństwa spółki", dopelniacz: "uruchomienia bezpieczeństwa spółki",
                kroki: ["konto", "zgody", "pracownicy", "monitorowanie", "reguly", "reaktor"],
                strefaGrozna: "grozne",
            }],
            slowa: ["reguły incydentów", "monitorowanie", "zgody aplikacji", "Czytelnik", "Reaktor", "data poinformowania pracowników"],
        }),

        // ── Ustawienia ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "ustawienia-uruchomienie", grupa: GRUPA_USTAWIEN, segment: { pl: "uruchomienie", en: "setup" },
            kluczTrasy: "adminIntUstUruchomienie",
            etykieta: "Uruchomienie", zdanie: "Tu widzisz, co zostało do zrobienia dla nowej spółki.",
            ikona: FiPlayCircle, wzor: "S4",
            slowa: ["kroki", "nowa spółka", "co zrobić po kolei"],
        }),
        obszar({
            id: "ustawienia-aktywnosc", grupa: GRUPA_USTAWIEN, segment: { pl: "zbieranie-aktywnosci", en: "activity" },
            kluczTrasy: "adminIntUstAktywnosc",
            etykieta: "Zbieranie aktywności", zdanie: "Tu decydujesz, czy i z których części Office 365 zbieramy aktywność osób.",
            ikona: FiActivity, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["monitoring aktywności", "czas pracy", "obecność", "zakres zbierania"],
        }),
        obszar({
            id: "ustawienia-geolokalizacja", grupa: GRUPA_USTAWIEN, segment: { pl: "geolokalizacja", en: "geolocation" },
            kluczTrasy: "adminIntUstGeolokalizacja",
            etykieta: "Geolokalizacja logowań", zdanie: "Tu decydujesz, czy przy logowaniach ustalamy kraj z adresu IP.",
            ikona: FiMapPin, wzor: "S3",
            slowa: ["geolokalizacja", "adres IP", "kraj logowania"],
        }),
        obszar({
            id: "ustawienia-ochrona", grupa: GRUPA_USTAWIEN, segment: { pl: "ochrona-tresci", en: "content-protection" },
            kluczTrasy: "adminIntUstOchrona",
            etykieta: "Ochrona treści", zdanie: "Tu włączasz blokady kopiowania, drukowania i zrzutów w aplikacji.",
            ikona: FiLock, wzor: "S3",
            slowa: ["znak wodny", "blokada wydruku", "blokada kopiowania", "ochrona ekranu"],
        }),
        obszar({
            id: "ustawienia-anonimowa", grupa: GRUPA_USTAWIEN, segment: { pl: "wersja-anonimowa", en: "anonymous-mode" },
            kluczTrasy: "adminIntUstAnonimowa",
            etykieta: "Wersja anonimowa", zdanie: "Tu ustawiasz maskowanie danych osób na czas nagrań i pokazów.",
            ikona: FiEyeOff, wzor: "S3",
            slowa: ["tryb nagrania", "maska", "maskuj telefony", "maskuj stopki"],
        }),
        obszar({
            id: "ustawienia-jak-dziala", grupa: GRUPA_USTAWIEN, segment: { pl: "jak-to-dziala", en: "how-it-works" },
            kluczTrasy: "adminIntUstJakDziala",
            etykieta: "Jak to działa", zdanie: "Tu czytasz, kto co robi i jak działają trzy rejestracje w Microsoft.",
            ikona: FiHelpCircle, wzor: "strona pomocy",
            slowa: ["pomoc", "przewodnik", "trzy rejestracje", "kto co robi", "Reaktor — jak zarejestrować"],
        }),
    ],

    trasyObiektow: [],

    /** Źródła listy „Do załatwienia” wypełnia paczka INT; plan: siedem LEKKICH odczytów z bazy aplikacji (5.2 projektu). */
    zrodlaStartu: [],
    sufitOdczytowStartu: 7,
    zrodlaStartuPlan: [
        { id: "poczta", odczyt: "GET /integrations/microsoft/diagnostics/mail-health", klucz: "AdminIntegrations" },
        { id: "synchronizacja", odczyt: "GET /integrations/o365/sync-status", klucz: "AdminIntegrations" },
        { id: "sms", odczyt: "GET /integration-hub/sms/messages", klucz: "AdminIntegrations" },
        { id: "logowanie", odczyt: "GET /lista-logowan", klucz: "AdminIntegrations" },
        { id: "polaczenia", odczyt: "GET /integration-hub/status", klucz: "AdminIntegrations" },
        { id: "bezpieczenstwo", odczyt: "GET /o365-reguly-spolek", klucz: "AdminIntegrations" },
        { id: "tryb-testowy", odczyt: "stan trybu testowego + lista skrzynek", klucz: "AdminIntegrations" },
    ],

    stareAdresy,
    /** Dawne osobne zakładki panelu → obszar tej strony (router panelu czyta to pole zamiast listy wpisanej w kod). */
    stareSlugi: { domains: "poczta", microsoft365: "synchronizacja-kont" },

    slownikPojec: [
        { slowo: "konto Office 365 spółki", definicja: "Połączenie aplikacji z Microsoft 365 jednej spółki, przez które działa logowanie, poczta i synchronizacja kont." },
        { slowo: "skrzynka", definicja: "Adres e-mail spółki, z którego aplikacja wysyła albo na który odbiera wiadomości." },
        { slowo: "przeznaczenie skrzynki", definicja: "Zadanie, do którego aplikacja używa skrzynki, na przykład powiadomienia, zgłoszenia albo sprawy kadrowe." },
        { slowo: "tryb testowy powiadomień", definicja: "Stan spółki, w którym jej e-maile idą na jedną wskazaną skrzynkę, a SMS-y nie wychodzą." },
        { slowo: "lista dozwolonych logowań", definicja: "Lista osób spółki, które mogą się zalogować do aplikacji, gdy lista jest włączona." },
        { slowo: "zgody aplikacji", definicja: "Pozwolenia nadane w Microsoft 365, dzięki którym aplikacja może czytać albo zmieniać dane spółki." },
        { slowo: "Czytelnik", definicja: "Rejestracja w Microsoft 365, która tylko czyta logowania i ustawienia potrzebne regułom bezpieczeństwa." },
        { slowo: "Reaktor", definicja: "Opcjonalna rejestracja w Microsoft 365, która pozwala aplikacji blokować konta i wykonywać inne reakcje." },
        { slowo: "data poinformowania pracowników", definicja: "Dzień, w którym spółka powiadomiła pracowników o monitorowaniu, wymagany przed jego włączeniem." },
        { slowo: "udostępnienie między spółkami", definicja: "Zgoda, żeby pracownicy jednej spółki widzieli okna zgłoszeń innej spółki." },
        { slowo: "konto bramki SMS", definicja: "Dane dostawcy SMS-ów jednej albo kilku spółek, przez które aplikacja wysyła wiadomości tekstowe." },
    ],

    gdzieCoBylo: [
        { bylo: "Skrzynki i tożsamość", jest: "Praca → Poczta", obszar: "poczta" },
        { bylo: "Tryb testowy przy skrzynkach", jest: "Praca → Tryb testowy powiadomień", obszar: "tryb-testowy" },
        { bylo: "Lista dozwolonych logowań (whitelista)", jest: "Praca → Kto może się logować", obszar: "logowanie" },
        { bylo: "Integracje techniczne", jest: "Połączenia → Systemy zewnętrzne — każdy system jako karta", obszar: "systemy" },
        { bylo: "Integracje techniczne → Synchronizacja skrzynek", jest: "Dane spółek → Skrzynki działów", obszar: "skrzynki-dzialow" },
        { bylo: "Integracje techniczne → Bramka SMS (historia wysyłek)", jest: "Praca → SMS-y", obszar: "sms" },
        { bylo: "Diagnostyka Microsoft 365", jest: "Połączenia → Systemy zewnętrzne → Microsoft 365 → Diagnostyka połączenia", obszar: "systemy" },
        { bylo: "Cross-org (firmy)", jest: "Dane spółek → Udostępnienia między spółkami", obszar: "udostepnienia" },
        { bylo: "Okno danych firmy", jest: "Dane spółek → Spółki i ich dane", obszar: "spolki" },
        { bylo: "Trzy przyciski „Synchronizuj teraz”", jest: "Połączenia → Synchronizacja kont — jeden przycisk", obszar: "synchronizacja-kont" },
        { bylo: "Licencje Microsoft 365", jest: "Połączenia → Licencje", obszar: "licencje" },
        { bylo: "Reguły incydentów", jest: "Połączenia → Bezpieczeństwo Office 365 — sześć kroków na spółkę", obszar: "bezpieczenstwo" },
        { bylo: "Monitoring aktywności", jest: "Ustawienia → Zbieranie aktywności", obszar: "ustawienia-aktywnosc" },
        { bylo: "Geolokalizacja logowań (przełącznik)", jest: "Ustawienia → Geolokalizacja logowań", obszar: "ustawienia-geolokalizacja" },
        { bylo: "Blokady treści (przełączniki)", jest: "Ustawienia → Ochrona treści", obszar: "ustawienia-ochrona" },
        { bylo: "Tryb nagrania", jest: "Ustawienia → Wersja anonimowa", obszar: "ustawienia-anonimowa" },
    ],

    stareNazwyMiejsc: [
        [/Integracje techniczne\s*(?:→|->)\s*Wykaz KSC/g, "Integracje → Systemy zewnętrzne → Wykaz KSC"],
        [/Integracje\s*(?:→|->)\s*Reguły incydentów/g, "Integracje → Bezpieczeństwo Office 365"],
        [/Integracje techniczne/g, "Integracje → Systemy zewnętrzne"],
        [/Skrzynki i tożsamość/g, "Integracje → Poczta"],
    ],
};

export default integracje;
